import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Users } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { TenantMember, TenantPendingInvite } from "@/services/tenantUsersService";

function getMemberDisplayName(m: {
  email: string;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}): string {
  if (m.displayName?.trim()) return m.displayName.trim();
  const parts = [m.firstName, m.lastName].filter(Boolean);
  if (parts.length) return parts.join(" ");
  return m.email;
}

function getMemberInitials(m: {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
}): string {
  if (m.firstName?.trim() && m.lastName?.trim()) {
    return `${m.firstName.trim()[0]}${m.lastName.trim()[0]}`.toUpperCase();
  }
  if (m.firstName?.trim()) return m.firstName.trim().slice(0, 2).toUpperCase();
  if (m.email.length >= 2) return m.email.slice(0, 2).toUpperCase();
  return m.email[0]?.toUpperCase() ?? "?";
}

export interface EstadoFiscalAccountantsCardProps {
  accountants: TenantMember[];
  pendingAccountantInvites: TenantPendingInvite[];
}

export function EstadoFiscalAccountantsCard({
  accountants,
  pendingAccountantInvites,
}: EstadoFiscalAccountantsCardProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasAccountants = accountants.length > 0;

  return (
    <Card className="border-border bg-muted/10 shadow-sm dark:bg-muted/5">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/15 dark:bg-primary/15"
            aria-hidden
          >
            <Users className="h-4 w-4" aria-hidden />
          </div>
          <p className="min-w-0 flex-1 text-sm font-semibold leading-tight text-foreground">
            {t("estadoFiscal.accountantsCardTitle")}
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {hasAccountants ? (
          <ul className="space-y-2.5">
            {accountants.map((m) => (
              <li key={m.userId}>
                <div
                  className={cn(
                    "flex flex-col gap-3 rounded-xl border border-border/70 bg-background/90 p-4 shadow-sm",
                    "transition-all duration-200 dark:bg-background/50",
                    "hover:border-primary/30 hover:shadow-md",
                    "motion-reduce:transition-none sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                  )}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <Avatar
                      className={cn(
                        "h-11 w-11 shrink-0 ring-2 ring-background",
                        "shadow-sm"
                      )}
                    >
                      {m.profileImage ? (
                        <AvatarImage src={m.profileImage} alt="" />
                      ) : null}
                      <AvatarFallback className="text-xs font-semibold text-muted-foreground">
                        {getMemberInitials(m)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <p className="truncate font-semibold leading-snug text-foreground">
                        {getMemberDisplayName(m)}
                      </p>
                      <p className="truncate text-xs leading-snug text-muted-foreground">
                        {m.email}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      navigate(`/users/${m.userId}`, { state: { member: m } })
                    }
                    className={cn(
                      "w-full shrink-0 gap-1.5 transition-colors duration-150",
                      "sm:w-auto"
                    )}
                    aria-label={`${t("estadoFiscal.accountantsViewProfile")}: ${getMemberDisplayName(m)}`}
                  >
                    {t("estadoFiscal.accountantsViewProfile")}
                    <ChevronRight className="h-4 w-4 opacity-70" aria-hidden />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="space-y-3">
            {pendingAccountantInvites.length > 0 ? (
              <>
                <Alert variant="warning">
                  <AlertDescription>
                    {t("estadoFiscal.accountantsPendingCount", {
                      count: pendingAccountantInvites.length,
                    })}
                  </AlertDescription>
                </Alert>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/users")}
                  className="transition-colors duration-150"
                >
                  {t("estadoFiscal.accountantsGoToUsers")}
                </Button>
              </>
            ) : (
              <div className="rounded-xl border border-dashed border-border/80 bg-muted/15 px-4 py-4 dark:bg-muted/10">
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {t("estadoFiscal.accountantsCardEmpty")}
                </p>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={() => navigate("/users")}
                  className="mt-3 transition-colors duration-150"
                >
                  {t("estadoFiscal.inviteAccountant")}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
